import {
  forwardRef,
  useImperativeHandle,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { Modal, Row, Col, Form, Input } from 'antd';
import { InputForm, SelectForm, Button } from 'components';
import LazySelectForm, {
  initLazySelectData,
  ILazySelectData,
  ILazySelectParams,
} from 'components/Select/LazySelectForm';
import { CustomerApis } from 'apis/CustomerApis';
import DebtApis from 'apis/DebtApis';
import { PlantApi } from 'apis/PlantApis';
import { useMutation } from '@tanstack/react-query';
import { toastSuccess, toastError } from 'utils/constants/functions';
import {
  handleOnChangeNumber,
  handleOnChangeNumberWithDecimal,
} from 'utils/constants/functions';

export interface AdjustDebtModalRef {
  open: () => void;
}

const AdjustDebtModal = forwardRef<AdjustDebtModalRef>((props, ref) => {
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [form] = Form.useForm();
  const [customerOptions, setCustomerOptions] =
    useState<ILazySelectData>(initLazySelectData);
  const [loadingCustomer, setLoadingCustomer] = useState(false);
  const [branchOptions, setBranchOptions] = useState<any[]>([]);

  useEffect(() => {
    if (open) {
      PlantApi.getAllPlantComboBox().then((res: any) => {
        const data = res?.data?.getPlant?.items || [];
        setBranchOptions(
          data.map((item: any) => ({
            label: item.name,
            value: item.id,
          })),
        );
      });
    }
  }, [open]);

  useImperativeHandle(ref, () => ({ open: () => setOpen(true) }));

  const handleScrollCustomersChange = useCallback(
    (params: ILazySelectParams) => {
      setLoadingCustomer(true);
      CustomerApis.getCustomersInCombobox({
        pageSize: params.pageSize,
        searchValue: params.value || '',
      })
        .then((res: any) => {
          const data = res?.data?.getCustomer;
          setCustomerOptions({
            options:
              data?.items?.map((item: any) => ({
                label:
                  (item?.sapCustomerId || item?.id) +
                  (item?.storeName ? ` - ${item?.storeName}` : ''),
                value: item?.sapCustomerId,
              })) || [],
            totalItems: data?.pageInfo?.totalItems || 0,
          });
        })
        .finally(() => setLoadingCustomer(false));
    },
    [],
  );

  const { mutate: updateDebt, isPending: updatingDebt } = useMutation({
    mutationFn: async (values: any) => {
      return await DebtApis.updateCustomerDebit({
        plantId: values.branch,
        sapCustomerId: values.customer,
        amountWagesTraditional: values.traditionalVnd ?? null,
        goldThreadQ10Traditional: values.traditionalQ10 ?? null,
        amountHighendLimit: values.premiumVnd ?? null,
        pnjpDebitQ10: values.pnjpQ10 ?? null,
        pnjpDebitAmount: values.pnjpVnd ?? null,
        note: values.note,
      });
    },
    onSuccess: (response) => {
      const result = response?.data?.updateSumPaymentCustomerDebit;
      if (result?.success) {
        toastSuccess(result?.message || 'Điều chỉnh công nợ thành công');
        setConfirmOpen(false);
        setOpen(false);
        form.resetFields();
        setCustomerOptions(initLazySelectData);
      } else {
        toastError(result?.message || 'Điều chỉnh công nợ thất bại');
      }
    },
    onError: (error: any) => {
      toastError(
        error?.response?.data?.message || 'Điều chỉnh công nợ thất bại',
      );
    },
  });

  const handleSubmit = async () => {
    setConfirmOpen(true);
  };

  const parseFormattedNumber = (value: string | undefined): number | null => {
    if (!value) return null;
    // Xóa tất cả dấu chấm và dấu phẩy, thay thế dấu phẩy bằng dấu chấm cho số thập phân
    const cleanedValue = value.toString().replace(/\./g, '').replace(',', '.');
    const parsed = parseFloat(cleanedValue);
    return isNaN(parsed) ? null : parsed;
  };

  const handleConfirm = async () => {
    const values = form.getFieldsValue();

    // Parse các giá trị đã format thành số
    const parsedValues = {
      ...values,
      traditionalQ10: parseFormattedNumber(values.traditionalQ10),
      traditionalVnd: parseFormattedNumber(values.traditionalVnd),
      premiumVnd: parseFormattedNumber(values.premiumVnd),
      pnjpQ10: parseFormattedNumber(values.pnjpQ10),
      pnjpVnd: parseFormattedNumber(values.pnjpVnd),
    };

    updateDebt(parsedValues);
  };

  const handleClose = () => {
    setOpen(false);
    form.resetFields();
    setCustomerOptions(initLazySelectData);
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={handleClose}
        title="Điều chỉnh công nợ"
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={[24, 0]}>
            {/* Chi nhánh */}
            <Col span={24}>
              <Form.Item
                label="Chi nhánh"
                name="branch"
                rules={[{ required: true, message: 'Vui lòng chọn chi nhánh' }]}
              >
                <SelectForm
                  placeholder="Chọn chi nhánh"
                  options={branchOptions}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>

            {/* Khách hàng */}
            <Col span={24}>
              <Form.Item label="Khách hàng" name="customer">
                <LazySelectForm
                  placeholder="Nhập tên hoặc mã KH"
                  data={customerOptions}
                  loading={loadingCustomer}
                  onSelectScrollChange={handleScrollCustomersChange}
                />
              </Form.Item>
            </Col>

            {/* Khách hàng nợ truyền thống */}
            <Col span={24}>
              <strong>Khách hàng nợ truyền thống</strong>
            </Col>
            <Col span={12}>
              <Form.Item label="Q10" name="traditionalQ10">
                <InputForm
                  addonAfter="Chỉ"
                  onChange={(e) => {
                    handleOnChangeNumberWithDecimal(e, form);
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="VND" name="traditionalVnd">
                <InputForm
                  addonAfter="VND"
                  onChange={(e) => {
                    handleOnChangeNumber(e, form);
                  }}
                />
              </Form.Item>
            </Col>

            {/* Khách hàng nợ cao cấp */}
            <Col span={24}>
              <strong>Khách hàng nợ cao cấp</strong>
            </Col>
            <Col span={12}>
              <Form.Item label="VND" name="premiumVnd">
                <InputForm
                  addonAfter="VND"
                  onChange={(e) => {
                    handleOnChangeNumber(e, form);
                  }}
                />
              </Form.Item>
            </Col>

            {/* PNJP nợ */}
            <Col span={24}>
              <strong>PNJP nợ</strong>
            </Col>
            <Col span={12}>
              <Form.Item label="Q10" name="pnjpQ10">
                <InputForm
                  addonAfter="Chỉ"
                  onChange={(e) => {
                    handleOnChangeNumberWithDecimal(e, form);
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="VND" name="pnjpVnd">
                <InputForm
                  addonAfter="VND"
                  onChange={(e) => {
                    handleOnChangeNumber(e, form);
                  }}
                />
              </Form.Item>
            </Col>

            {/* Ghi chú */}
            <Col span={24}>
              <Form.Item
                label="Ghi chú *"
                name="note"
                rules={[{ required: true, message: 'Vui lòng nhập ghi chú' }]}
              >
                <Input.TextArea rows={4} placeholder="Nhập ghi chú" />
              </Form.Item>
            </Col>

            {/* Buttons */}
            <Col span={24} style={{ marginBottom: 16 }}>
              <Row gutter={8} justify={'end'}>
                <Col>
                  <Button
                    title="Quay lại"
                    type="button"
                    handleOnclick={handleClose}
                  />
                </Col>
                <Col>
                  <Button
                    title="Xác nhận"
                    fill
                    handleOnclick={() => form.submit()}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </Modal>
      <Modal
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        title="Xác nhận"
        footer={null}
      >
        <p>Công nợ sẽ được điều chỉnh. Bạn có chắc muốn điều chỉnh công nợ?</p>
        <Row gutter={8} justify={'end'} style={{ marginTop: 16 }}>
          <Col>
            <Button
              title="Huỷ"
              type="button"
              handleOnclick={() => setConfirmOpen(false)}
            />
          </Col>
          <Col>
            <Button
              title="Xác nhận"
              fill
              handleOnclick={handleConfirm}
              loading={updatingDebt}
            />
          </Col>
        </Row>
      </Modal>
    </>
  );
});

export default AdjustDebtModal;
